import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '@trovata/app/core/models/config.model';
import {
	ProfilesApiResponse,
	WorksheetImportResponse,
	WorksheetLoadResponse,
	WorksheetMode,
	WorksheetSaveResponse,
	WorksheetSaveSheet,
} from '../models/worksheet-app.model';
import { WorksheetCatalogResponse, WorksheetDimensionsResponse, WorksheetMembersResponse } from '../models/worksheet-catalog.model';

/**
 * HTTP calls to ATOM WorksheetDataServlet (/api/v1/worksheet) — the 6th TMS
 * app type. All Phase-1 actions are GET / read-only.
 *
 * <p>Auth is handled by AuthInterceptor (atomApiUrl + {@code /api/*} are in the
 * {@code isApi} allowlist — no manual header pattern; Jackson's Rules §2). Each
 * method just returns the Observable from {@code http.get(...)}.
 */
@Injectable({ providedIn: 'root' })
export class WorksheetAppApiService {
	private readonly baseUrl: string;

	constructor(
		private readonly http: HttpClient,
		@Inject(ENVIRONMENT_TOKEN) private readonly environment: DefaultEnvironment
	) {
		this.baseUrl = `${this.environment.atomApiUrl}/api/v1/worksheet`;
	}

	/** List the worksheet app's profiles (SEC_USER_PROFILE, keyed by app name). */
	getProfiles(app: string): Observable<ProfilesApiResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'profiles');
		return this.http.get<ProfilesApiResponse>(this.baseUrl, { params });
	}

	/**
	 * Load a profile's grid. Execute mode → computed values (read-only); build
	 * mode → raw formula text (editable). Cell structure is returned in both.
	 */
	load(app: string, profileCd?: string, mode: WorksheetMode = 'execute'): Observable<WorksheetLoadResponse> {
		let params: HttpParams = new HttpParams().set('app', app).set('action', 'load');
		if (profileCd) {
			params = params.set('profile', profileCd);
		}
		if (mode === 'build') {
			params = params.set('mode', 'build');
		}
		return this.http.get<WorksheetLoadResponse>(this.baseUrl, { params });
	}

	/** Persist the client-authored grid (POST ?action=save). */
	save(app: string, profileCd: string, sheets: ReadonlyArray<WorksheetSaveSheet>): Observable<WorksheetSaveResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'save');
		const body: { profile: string; sheets: ReadonlyArray<WorksheetSaveSheet> } = { profile: profileCd, sheets };
		return this.http.post<WorksheetSaveResponse>(this.baseUrl, body, { params });
	}

	/**
	 * Parse an `.xlsx` (POST multipart `?action=import`) — **parse-only, no DB
	 * write**. Returns the parsed sheets (save-shape) for an UNSAVED draft profile;
	 * the client persists later via {@link #saveAsNew} (the Update button). Build-mode
	 * entitlement is enforced server-side.
	 */
	import(app: string, file: File, profileName: string): Observable<WorksheetImportResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'import');
		const form: FormData = new FormData();
		form.append('file', file, file.name);
		form.append('profileName', profileName);
		return this.http.post<WorksheetImportResponse>(this.baseUrl, form, { params });
	}

	/**
	 * Persist an unsaved draft as a NEW profile (POST `?action=save-as-new`) — the
	 * Update action. The server rejects a duplicate name with 400 (never overwrites).
	 */
	saveAsNew(app: string, profileName: string, sheets: ReadonlyArray<WorksheetSaveSheet>): Observable<WorksheetImportResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'save-as-new');
		const body: { profile: string; sheets: ReadonlyArray<WorksheetSaveSheet> } = { profile: profileName, sheets };
		return this.http.post<WorksheetImportResponse>(this.baseUrl, body, { params });
	}

	/**
	 * Live-refresh recompute. Phase 1 is a full recompute (identical shape to
	 * {@link #load}); the legacy changes-only diff needs server-held state and
	 * is deferred to a later phase.
	 */
	refresh(app: string, profileCd?: string): Observable<WorksheetLoadResponse> {
		let params: HttpParams = new HttpParams().set('app', app).set('action', 'refresh');
		if (profileCd) {
			params = params.set('profile', profileCd);
		}
		return this.http.get<WorksheetLoadResponse>(this.baseUrl, { params });
	}

	/**
	 * Flush all Mondrian caches and restart Mondrian (admin only — the server
	 * enforces; the button is also hidden for non-admins). Same primitive the
	 * legacy Flush Cubes button drives.
	 */
	flushCubes(appName: string): Observable<WorksheetSaveResponse> {
		const params: HttpParams = new HttpParams().set('app', appName).set('action', 'flushCubes');
		return this.http.get<WorksheetSaveResponse>(this.baseUrl, { params });
	}

	/** ATOMXLS builder — all topic (measure) names for the Topic picker. */
	getCatalog(app: string): Observable<WorksheetCatalogResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'catalog');
		return this.http.get<WorksheetCatalogResponse>(this.baseUrl, { params });
	}

	/** ATOMXLS builder — the dimensions (name + type) of a topic's cube. */
	getDimensions(app: string, topic: string): Observable<WorksheetDimensionsResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'dimensions').set('topic', topic);
		return this.http.get<WorksheetDimensionsResponse>(this.baseUrl, { params });
	}

	/** ATOMXLS builder — member values for a (topic-cube, dimension). */
	getDimensionMembers(app: string, topic: string, dimension: string): Observable<WorksheetMembersResponse> {
		const params: HttpParams = new HttpParams().set('app', app).set('action', 'dimensionMembers').set('topic', topic).set('dimension', dimension);
		return this.http.get<WorksheetMembersResponse>(this.baseUrl, { params });
	}
}
